import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { CONFIG } from '../config/constants'
import GoogleFormDialog from '../components/GoogleFormDialog'

const Bookings: React.FC = () => {
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)

  return (
    <div className="section-padding">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-4">
            Book Your Appointment
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-4">
            Schedule your consultation with our expert physiotherapists
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="card p-5 sm:p-6 lg:p-8"
          >
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">🩺 Book Your Appointment</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Choose your preferred booking method below. We'll contact you shortly to confirm your appointment.
            </p>
            
            {CONFIG.googleFormEmbedUrl ? (
              <div className="space-y-4">
                {/* Quick Booking Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsFormDialogOpen(true)}
                  className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl shadow-lg transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <span className="text-2xl">📋</span>
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-lg">Quick Online Booking</div>
                      <div className="text-sm text-primary-100">Fill out our appointment form</div>
                    </div>
                  </div>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.button>

                {/* Alternative Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href={`https://wa.me/${CONFIG.whatsappNumber}?text=Hi, I would like to book an appointment`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors border border-green-200 dark:border-green-800"
                  >
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white text-sm">WhatsApp</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">Quick chat</div>
                    </div>
                  </motion.a>

                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href={`tel:${CONFIG.phone}`}
                    className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-800"
                  >
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white text-sm">Call Us</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">Direct call</div>
                    </div>
                  </motion.a>
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold text-primary-700 dark:text-primary-400">💡 Quick Tip:</span> For fastest response, use the online booking form above or contact us via WhatsApp.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  To enable online booking, please add your Google Form embed URL to the CONFIG object in constants.ts
                </p>
                <a
                  href="https://forms.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Create Google Form
                </a>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="card p-5 sm:p-6 lg:p-8"
          >
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">📋 Booking Information</h3>
            
            <div className="space-y-6">
              {/* Clinic Timings */}
              <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-primary-600 dark:text-primary-400">⏰</span>
                  Clinic Timings
                </h4>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 ml-8">
                  <div className="flex justify-between">
                    <span className="font-medium">Monday - Saturday:</span>
                    <span>9 AM - 1:30 PM / 4 PM - 8 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Sunday:</span>
                    <span>Closed / Appointment Only</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-primary-600 dark:text-primary-400">📞</span>
                  Contact Information
                </h4>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300 ml-8">
                  <div>
                    <strong className="text-gray-900 dark:text-white">Phone:</strong>
                    <div className="mt-1">
                      <a href={`tel:${CONFIG.phone}`} className="text-primary-600 hover:text-primary-700 dark:text-primary-400">
                        {CONFIG.phone}
                      </a>
                    </div>
                  </div>
                  <div>
                    <strong className="text-gray-900 dark:text-white">Email:</strong>
                    <div className="mt-1">
                      <a href={`mailto:${CONFIG.email}`} className="text-primary-600 hover:text-primary-700 dark:text-primary-400">
                        {CONFIG.email}
                      </a>
                    </div>
                  </div>
                  <div>
                    <strong className="text-gray-900 dark:text-white">WhatsApp:</strong>
                    <div className="mt-1">
                      <a 
                        href={`https://wa.me/${CONFIG.whatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                      >
                        +91 {CONFIG.phone.replace('+91-', '')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-primary-600 dark:text-primary-400">📍</span>
                  Clinic Address
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-300 ml-8">
                  <p className="leading-relaxed">{CONFIG.address}</p>
                  <a 
                    href={CONFIG.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
                  >
                    <span>📍</span>
                    View on Google Maps
                  </a>
                </div>
              </div>

              {/* Appointment Info */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-primary-600 dark:text-primary-400">✅</span>
                  Appointment Details
                </h4>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 ml-8">
                  <p>• Fill out the online booking form with your details</p>
                  <p>• Include your reason for appointment and preferred time</p>
                  <p>• We'll contact you within 24 hours to confirm your appointment</p>
                  <p>• For urgent appointments, please call us directly</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Google Form Dialog */}
        {CONFIG.googleFormEmbedUrl && (
          <GoogleFormDialog
            isOpen={isFormDialogOpen}
            onClose={() => setIsFormDialogOpen(false)}
            formUrl={CONFIG.googleFormEmbedUrl}
            title="Book Your Appointment"
          />
        )}
      </div>
    </div>
  )
}

export default Bookings
